import * as React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  makeStyles,
  Spinner,
  tokens,
} from "@fluentui/react-components";

import { useAuth } from "../../auth/AuthProvider";
import { AppRole } from "../../api/types";
import ErrorPanel from "./shared/ErrorPanel";
import { RoleProvider } from "./RoleContext";
import HomeLayout from "./layouts/HomeLayout";
import ModuleLayout from "./layouts/ModuleLayout";
import HomePage from "./pages/HomePage";
import ArticleEnrichmentPage from "./modules/article-enrichment/ArticleEnrichmentPage";

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column"
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "16px",
    paddingBottom: "12px",
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  centered: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    gap: "12px"
  },
});

const App: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated, isLoading, error, userRoles } = useAuth();

  const validRoles = userRoles.filter(
    (r) => r === AppRole.Sales || r === AppRole.Purchase
  );

  const location = useLocation();
  const isHome = location.pathname === "/";
  const prevIsHome = React.useRef(isHome);

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.centered}>
          <Spinner label="Authenticating…" size="small" />
        </div>
      </div>
    );
  }

  // Auth error
  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.centered}>
          <ErrorPanel title="Authentication Error" message={error} />
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className={styles.root}>
        <div className={styles.centered}>
          <ErrorPanel message="You are not signed in. Please sign in to use this add-in." />
        </div>
      </div>
    );
  }

  // No valid role
  if (validRoles.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.centered}>
          <ErrorPanel
            title="Access Denied"
            message="Your account does not have a Sales or Purchase role assigned. Please contact your administrator."
          />
        </div>
      </div>
    );
  }

  let direction = 0;
  if (prevIsHome.current !== isHome) {
    direction = isHome ? -1 : 1;
  }

  return (
    <RoleProvider validRoles={validRoles}>
      <div style={{ position: "relative", overflow: "hidden", minHeight: "100vh" }}>
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={isHome ? "home" : "module"}
            custom={direction}
            initial="enter"
            animate="center"
            exit="exit"
            variants={{
              enter: (dir: number) => ({ x: `${dir * 100}%` }),
              center: { x: 0 },
              exit: (dir: number) => ({ x: `${dir * -100}%` }),
            }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ position: "absolute", width: "100%", minHeight: "100vh" }}
          >
            <Routes location={location}>
              <Route element={<HomeLayout />}>
                <Route index element={<HomePage />} />
              </Route>
              <Route path="article-enrichment" element={<ModuleLayout title="Article Enrichment" />}>
                <Route index element={<ArticleEnrichmentPage />} />
              </Route>
            </Routes>
          </motion.div>
        </AnimatePresence>
      </div>
    </RoleProvider>
  );
};

export default App;
